// frontend/src/components/charts/BurndownChart.js
import React, { useMemo } from 'react';
import {
  Box, Typography, Paper, useTheme, useMediaQuery,
  CircularProgress, Alert, Stack, Chip
} from '@mui/material';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { TrendingDown, CheckCircle, Warning } from '@mui/icons-material';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const BurndownChart = ({
  data,
  loading,
  error,
  project,
  height = 400,
  showIdeal = true,
  showActual = true,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const chartData = useMemo(() => {
    if (!data?.ideal_burndown?.length && !data?.actual_burndown?.length) {
      return { labels: [], datasets: [] };
    }

    // Combine dates from both datasets
    const allDates = [...new Set([
      ...(data.ideal_burndown?.map(d => d.date) || []),
      ...(data.actual_burndown?.map(d => d.date) || [])
    ])].sort();

    const datasets = [];

    // Ideal Burndown Line (dashed)
    if (showIdeal && data.ideal_burndown?.length) {
      datasets.push({
        label: 'Ideal Progress',
        data: allDates.map(date => {
          const point = data.ideal_burndown.find(d => d.date === date);
          return point ? point.ideal_remaining : null;
        }),
        borderColor: theme.palette.grey[500],
        borderDash: [5, 5],
        borderWidth: 2,
        pointRadius: 0,
        pointHoverRadius: 4,
        fill: false,
        tension: 0.1,
      });
    }

    // Actual Burndown Line (solid)
    if (showActual && data.actual_burndown?.length) {
      datasets.push({
        label: 'Actual Progress',
        data: allDates.map(date => {
          const point = data.actual_burndown.find(d => d.date === date);
          return point ? point.actual_remaining : null;
        }),
        borderColor: theme.palette.primary.main,
        backgroundColor: `${theme.palette.primary.main}20`,
        borderWidth: 3,
        pointRadius: 4,
        pointHoverRadius: 6,
        pointBackgroundColor: theme.palette.primary.main,
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
        fill: true,
        tension: 0.3,
      });
    }

    return {
      labels: allDates.map(date => {
        const d = new Date(date);
        return isMobile
          ? `${d.getDate()}/${d.getMonth() + 1}`
          : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      }),
      datasets,
    };
  }, [data, showIdeal, showActual, theme, isMobile]);

  const chartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: isMobile ? 'bottom' : 'top',
        labels: {
          usePointStyle: true,
          padding: 20,
        },
      },
      title: {
        display: true,
        text: `Burndown Chart - ${project?.name || 'Project'}`,
        font: { size: 16, weight: 'bold' },
        padding: { top: 10, bottom: 20 },
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        callbacks: {
          label: (context) => {
            const value = context.parsed.y;
            return value !== null ? `${context.dataset.label}: ${value} pts` : null; // Changed from "hrs" to "pts"
          },
        },
      },
    },
    scales: {
      x: {
        title: {
          display: true,
          text: 'Date',
          font: { weight: 'bold' },
        },
        grid: { display: false },
      },
      y: {
        title: {
          display: true,
          text: 'Remaining Story Points',
          font: { weight: 'bold' },
        },
        beginAtZero: true,
        min: 0,
        grid: {
          color: theme.palette.divider,
        },
      },
    },
    interaction: {
      mode: 'nearest',
      axis: 'x',
      intersect: false,
    },
  }), [project?.name, isMobile, theme]);

  // Calculate chart status
  const getStatus = () => {
    if (!data?.actual_burndown?.length) return null;

    const latest = data.actual_burndown[data.actual_burndown.length - 1];
    const idealLatest = data.ideal_burndown?.find(d => d.date === latest.date);

    if (!idealLatest) return { status: 'unknown', message: 'Calculating...' };

    const diff = latest.actual_remaining - idealLatest.ideal_remaining;

    if (diff > 10) {
      return {
        status: 'behind',
        message: `Behind schedule by ${diff.toFixed(1)} hours`,
        color: 'error'
      };
    } else if (diff < -10) {
      return {
        status: 'ahead',
        message: `Ahead of schedule by ${Math.abs(diff).toFixed(1)} hours`,
        color: 'success'
      };
    }
    return {
      status: 'on-track',
      message: 'On track with schedule',
      color: 'info'
    };
  };

  const status = getStatus();

  if (loading) {
    return (
      <Paper sx={{ p: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: height }}>
        <CircularProgress />
      </Paper>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ borderRadius: 2 }}>
        Failed to load burndown data: {error}
      </Alert>
    );
  }

  if (!data?.ideal_burndown?.length && !data?.actual_burndown?.length) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
        <TrendingDown sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
        <Typography variant="h6" gutterBottom>No burndown data available</Typography>
        <Typography color="text.secondary">
          Add tasks with estimated hours to see the burndown chart.
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: 2, boxShadow: 2 }}>
      {/* Status Indicator */}
      {status && (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
          <Chip
            icon={status.status === 'ahead' ? <CheckCircle /> : status.status === 'behind' ? <Warning /> : <TrendingDown />}
            label={status.message}
            color={status.color}
            size="small"
            variant="outlined"
          />
          <Typography variant="body2" color="text.secondary">
            Remaining: <strong>{data.remaining_work} hrs</strong> / Total: <strong>{data.total_estimated_hours} hrs</strong>
          </Typography>
        </Stack>
      )}

      {/* Chart */}
      <Box sx={{ height, position: 'relative' }}>
        <Line data={chartData} options={chartOptions} />
      </Box>

      {/* Legend Explanation */}
      <Box sx={{ mt: 2, display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ width: 20, height: 3, bgcolor: theme.palette.grey[500], borderStyle: 'dashed', borderWidth: 1 }} />
          <Typography variant="caption">Ideal Progress</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ width: 20, height: 3, bgcolor: theme.palette.primary.main }} />
          <Typography variant="caption">Actual Progress</Typography>
        </Box>
      </Box>
    </Paper>
  );
};

export default BurndownChart;